'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Input,
  Select,
  SelectItem,
  Textarea,
  Autocomplete,
  AutocompleteItem,
} from '@heroui/react';
import useClientStore from '@/store/useClientStore';
import useCustomerStore from '@/store/useCustomerStore';
import { supabase } from '@/lib/supabase';
import { submitLeadIntake } from '@/lib/lead-intake';
import { pedidoAutoDelCliente } from '@/lib/auto-cliente-intake';
import { LeadTypes, Brand, Model, Condition, Vehicle } from '@/utils/types';
import { sendEmail, createVehicleLeadEmailTemplate } from '@/lib/send-email';
import { sendSiteFormEmail, idNumerico } from '@/lib/site-form-email';
import { normalizarPatente, notasConPatente } from '@/lib/patente';
import SuccessModal from '@/components/ui/SuccessModal';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { useCurrency } from '@/hooks/useCurrency';
import { isBrasil, appraiserOnSite, forcedSiteLanguage } from '@/lib/country';
import { etiquetaCatalogo } from '@/utils/functions';

/**
 * Qué hace el formulario con el precio del auto (ticket #49). Lo elige la
 * automotora desde el Builder:
 *
 *   'visitante' → le pide el precio a quien completa. Es lo que hacía este
 *                 formulario desde siempre, así que es el default: los sitios
 *                 ya publicados no cambian solos.
 *   'tasador'   → no pide precio; le manda una estimación por correo.
 *   'ninguno'   → no pide ni estima. Captación pura.
 *
 * El default NO es 'tasador' a propósito: una automotora puede no querer que le
 * tasen autos en su web, y encenderlo para todos sin avisar sería decidir por
 * ella. Se prende a mano.
 */
export type PrecioModo = 'visitante' | 'tasador' | 'ninguno';

interface FormStyleProps {
  title?: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
  embedded?: boolean;
  precioModo?: PrecioModo;
}

const BuyDirectForm = ({ title, subtitle, bgColor, textColor, accentColor, embedded = false, precioModo: precioModoProp = 'visitante' }: FormStyleProps = {}) => {
  const { client } = useClientStore();
  const esBrasil = isBrasil(client?.country);
  // EE.UU.: lo que lee la automotora (asunto del correo), en inglés.
  const enUsa = forcedSiteLanguage(client?.country) === 'en';
  // El tasador por patente (GetAPI) es un servicio 100% chileno: en los países
  // sin tasador en el sitio (`appraiserOnSite`: Brasil, Colombia, Argentina) el
  // modo 'tasador' no existe y el formulario queda como el de siempre
  // ('visitante': pide el precio). Chile y sin país: exactamente como antes.
  const precioModo: PrecioModo | string =
    !appraiserOnSite(client?.country) && precioModoProp === 'tasador' ? 'visitante' : precioModoProp;
  const pidePrecio = precioModo === 'visitante';
  const tasaAutomatico = precioModo === 'tasador';
  const { locale, symbol } = useCurrency();
  // When builder passes bgColor, use inline styles. Otherwise use default Tailwind classes.
  const hasBuilderStyles = !!bgColor;
  const isDarkBg = bgColor && (bgColor.startsWith('#0') || bgColor.startsWith('#1') || bgColor.startsWith('#2'));
  const cardStyle = hasBuilderStyles
    ? { backgroundColor: bgColor, borderColor: textColor ? `${textColor}15` : undefined }
    : undefined;
  const cardClass = hasBuilderStyles
    ? 'rounded-xl shadow-lg p-8 border'
    : 'bg-white dark:bg-dark-card rounded-xl shadow-lg p-8 border border-gray-200 dark:border-dark-border';
  const infoCardStyle = hasBuilderStyles
    ? { backgroundColor: bgColor ? `${bgColor}f0` : undefined, borderColor: textColor ? `${textColor}15` : undefined }
    : undefined;
  const infoCardClass = hasBuilderStyles
    ? 'rounded-xl p-8 border'
    : 'bg-gray-50 dark:bg-dark-card rounded-xl p-8 border border-gray-200 dark:border-dark-border';
  const titleStyle = hasBuilderStyles ? { color: textColor } : undefined;
  const titleClass = hasBuilderStyles ? 'text-3xl sm:text-4xl font-extrabold' : 'text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white';
  const subtitleStyle = hasBuilderStyles ? { color: textColor, opacity: 0.6 } : undefined;
  const subtitleClass = hasBuilderStyles ? 'mt-4 text-lg' : 'mt-4 text-lg text-gray-500 dark:text-gray-400';
  const headingStyle = hasBuilderStyles ? { color: textColor } : undefined;
  const headingClass = hasBuilderStyles ? 'text-2xl font-bold' : 'text-2xl font-bold text-gray-900 dark:text-white';
  const subHeadingClass = hasBuilderStyles ? 'text-lg font-medium' : 'text-lg font-medium text-gray-900 dark:text-white';
  const bodyStyle = hasBuilderStyles ? { color: textColor, opacity: 0.7 } : undefined;
  const bodyClass = hasBuilderStyles ? '' : 'text-gray-600 dark:text-gray-400';

  // HeroUI input classNames to match builder theme
  const inputClassNames = (hasBuilderStyles || embedded) ? {
    label: isDarkBg ? '!text-white/60' : '!text-black/50',
    input: isDarkBg ? '!text-white !placeholder-white/40' : '!text-gray-900',
    inputWrapper: isDarkBg
      ? '!bg-[#262626] !border-[#3a3a3a] hover:!border-[#4a4a4a] !rounded-lg'
      : '!bg-white !border-[#d1d5db] hover:!border-gray-400 !rounded-lg',
  } : undefined;
  const buttonStyle = (hasBuilderStyles || embedded) && accentColor ? { backgroundColor: accentColor } : undefined;

  const { initializeCustomer } = useCustomerStore();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // Se marca recién al intentar enviar: no tiene sentido gritarle "patente
  // inválida" a alguien que todavía la está escribiendo.
  const [patenteInvalida, setPatenteInvalida] = useState(false);
  const patenteRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    brand_id: '',
    model_id: '',
    vehicle_year: '',
    vehicle_mileage: '',
    condition_id: '',
    asking_price: '',
    message: '',
    license_plate: '',
    version: '',
  });

  // Fetch brands, models, and conditions on component mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch brands
      const { data: brandsData } = await supabase.from('brands').select('*');
      if (brandsData) setBrands(brandsData);

      // Fetch conditions
      const { data: conditionsData } = await supabase
        .from('conditions')
        .select('*');
      if (conditionsData) setConditions(conditionsData);
    };

    fetchData();
  }, []);

  // Fetch models when brand changes
  useEffect(() => {
    if (selectedBrandId) {
      const fetchModels = async () => {
        const { data } = await supabase
          .from('models')
          .select('*')
          .eq('brand_id', selectedBrandId);

        if (data) setModels(data);
      };

      fetchModels();
    } else {
      setModels([]);
    }
  }, [selectedBrandId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // En modo tasador la patente es obligatoria (ticket #90): con ella la edge
    // saca el año y la versión reales del auto. Se valida antes de crear nada
    // para no dejar un lead a medias por un error de tipeo.
    const patente = tasaAutomatico
      ? normalizarPatente(formData.license_plate, client?.country)
      : null;
    if (tasaAutomatico && !patente) {
      setPatenteInvalida(true);
      // En el celular el campo queda muy arriba del botón: sin llevarlo a la
      // vista, parece que el botón no hizo nada.
      requestAnimationFrame(() => {
        patenteRef.current?.focus();
        patenteRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      return;
    }
    const version = formData.version.trim();
    // Lo que escribió la persona, con la patente y la versión arriba en modo
    // tasador. Va a las notas del lead y al correo de aviso a la automotora.
    const notas =
      tasaAutomatico && patente
        ? notasConPatente(formData.message, patente, version, client?.country)
        : formData.message;

    try {
      setLoading(true);

      // 1. Create/initialize customer (con nombre y teléfono: la edge del paso 2
      // lo encuentra por el email y no lo sobrescribe).
      await initializeCustomer({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        client_id: client?.id || '',
      });

      // 2. Lead + el auto que ofrece el cliente, en UNA sola llamada a
      // public-lead-intake (ticket #96). El auto es del cliente, no de la
      // automotora: la edge lo guarda dentro del lead y no en `vehicles`, donde
      // aparecía como un auto más del inventario. En los tres modos: antes
      // 'visitante' y 'ninguno' pedían el auto solo e insertaban el lead con el
      // anon key, y el modo tasador necesita igual el id del lead para pegarle
      // la tasación (el anon key no tiene SELECT sobre `leads`).
      const intake = await submitLeadIntake(
        pedidoAutoDelCliente({
          clientId: client?.id || '',
          tipo: LeadTypes.BUY_DIRECT,
          notas,
          cliente: formData,
          auto: {
            brand_id: formData.brand_id,
            model_id: formData.model_id,
            year: formData.vehicle_year,
            mileage: formData.vehicle_mileage,
            condition_id: formData.condition_id,
            asking_price: formData.asking_price,
          },
        })
      );
      const nuevoLeadId: number | null = intake.lead_id;
      if (!nuevoLeadId) throw new Error('No se pudo crear el lead');

      // 3b. Tasación (sólo en modo tasador).
      //
      // Se dispara DESPUÉS de tener el lead guardado y no se espera el
      // resultado: el tasador tarda 34 s de mediana y hasta 89 s, así que
      // bloquear el formulario sería dejar a la persona mirando un spinner
      // medio minuto. La estimación le llega por correo.
      //
      // Si esto falla, el lead ya existe y la automotora atiende igual: por eso
      // el error se registra y no se le muestra a quien completó el formulario.
      const selectedBrand =
        brands.find((b) => b.id === formData.brand_id)?.name || '';
      const selectedModel =
        models.find((m) => m.id === parseInt(formData.model_id))?.name || '';
      const selectedCondition =
        conditions.find((c) => c.id === parseInt(formData.condition_id))
          ?.name || '';

      if (tasaAutomatico && nuevoLeadId) {
        // Ni el correo ni el nombre van en el cuerpo: la edge los saca del
        // cliente del lead. Si los tomara de acá, sería un relay para mandar
        // correos desde un dominio de GoAuto a cualquier dirección.
        const { error: appraisalError } = await supabase.functions.invoke(
          'public-appraisal',
          {
            body: {
              client_id: client?.id,
              lead_id: nuevoLeadId,
              vehicle: {
                brand: selectedBrand,
                model: selectedModel,
                year: parseInt(formData.vehicle_year),
                mileage: parseInt(formData.vehicle_mileage),
                // Con la patente la edge busca el año y la versión reales; si
                // no la encuentra, tasa con lo que se escribió acá.
                license_plate: patente,
                ...(version ? { version } : {}),
              },
            },
          }
        );

        // `invoke` NO tira excepción ante un 4xx: devuelve `{ error }`. Sin
        // mirarlo, el caso más probable (tope alcanzado, lead sin correo) se
        // perdía sin dejar rastro. No se le muestra nada a la persona: el lead
        // ya está guardado y la automotora la va a contactar igual.
        if (appraisalError) {
          console.error('No se pudo pedir la tasación:', appraisalError);
        }
      }

      // 4. Send email notification

      // Format price with thousands separators
      const formattedPrice = formData.asking_price
        ? parseInt(formData.asking_price.replace(/\D/g, '')).toLocaleString(
            locale
          )
        : '';

      // Usar la función de email
      const datosCorreo = {
        leadType: LeadTypes.BUY_DIRECT,
        customerName: `${formData.first_name} ${formData.last_name}`,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        vehicleDetails: {
          brand: selectedBrand,
          model: selectedModel,
          year: formData.vehicle_year,
          mileage: formData.vehicle_mileage,
          // Solo se muestra en el correo: Brasil en pt; Chile, el de siempre.
          condition: etiquetaCatalogo('condition', selectedCondition, client?.country, selectedCondition),
          price: formattedPrice,
        },
        additionalMessage: notas,
        country: client?.country,
        currencySymbol: symbol,
      };
      const emailContent = createVehicleLeadEmailTemplate(datosCorreo);

      // Determinar emails de destino para compras
      const buyEmails =
        client?.contact?.buy_emails && client.contact.buy_emails.length > 0
          ? client.contact.buy_emails
          : [client?.contact?.email || ''];

      // Destinatarios/asunto/HTML los arma la edge (`site_form`); si no
      // puede, cae a este mismo envío de siempre. El asunto sale de
      // params.vehicleDetails (brand, model, year).
      const emailResult = await sendSiteFormEmail(
        {
          client_id: idNumerico(client?.id) ?? 0,
          kind: 'lead_buy',
          vehicle_id: null,
          params: datosCorreo,
        },
        {
          to: buyEmails,
          // Lo lee la automotora: en Brasil, en portugués; en EE.UU., en inglés.
          subject: esBrasil
            ? `Solicitação de compra: ${selectedBrand} ${selectedModel} (${formData.vehicle_year})`
            : enUsa
              ? `Vehicle purchase request: ${selectedBrand} ${selectedModel} (${formData.vehicle_year})`
              : `Solicitud de Compra: ${selectedBrand} ${selectedModel} (${formData.vehicle_year})`,
          content: emailContent,
        },
        sendEmail
      );

      if (!emailResult.success) {
        console.error('Error al enviar el email:', emailResult.error);
        // Continuar con el flujo aunque el email falle
        // La solicitud ya se guardó en la base de datos
      }

      // Clear form after successful submission
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        brand_id: '',
        model_id: '',
        vehicle_year: '',
        vehicle_mileage: '',
        condition_id: '',
        asking_price: '',
        message: '',
        license_plate: '',
        version: '',
      });

      // Mostrar modal de éxito en lugar de alert
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error al procesar el formulario:', error);

      // Mensaje más específico según el tipo de error
      let errorMessage = t('formsUi.errors.generic');

      if (error.message?.includes('email')) {
        errorMessage = t('formsUi.errors.emailNotSent');
      } else if (error.code === '23505') {
        errorMessage = t('formsUi.errors.duplicate');
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value: string, id: string) => {
    if (id === 'asking_price') {
      // Remove non-numeric characters
      const numericValue = value.replace(/\D/g, '');

      // Format with thousand separators
      const formattedValue =
        numericValue === '' ? '' : Number(numericValue).toLocaleString(locale);

      setFormData((prev) => ({
        ...prev,
        [id]: formattedValue,
      }));
    } else if (id === 'license_plate') {
      setPatenteInvalida(false);
      // Se guarda tal cual y se muestra en mayúsculas por CSS: pasarla a
      // mayúsculas en cada tecla, en un input controlado, duplica letras con
      // la predicción de algunos teclados de Android. Se normaliza al enviar.
      setFormData((prev) => ({ ...prev, license_plate: value }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }

    // Update selected brand when brand changes
    if (id === 'brand_id') {
      setSelectedBrandId(value);
      // Reset model when brand changes
      setFormData((prev) => ({ ...prev, model_id: '' }));
    }
  };

  return (
    <div data-form-section="buy-direct">
      {!embedded && title && (
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <h1 className={titleClass} style={titleStyle}>{title}</h1>
          {subtitle && <p className={subtitleClass} style={subtitleStyle}>{subtitle}</p>}
        </div>
      )}
      <div className={embedded ? '' : (hasBuilderStyles ? 'max-w-2xl mx-auto' : 'grid grid-cols-1 md:grid-cols-2 gap-16')}>
        {/* Sale Form */}
        <div className={embedded ? '' : cardClass} style={embedded ? undefined : cardStyle}>
          <form onSubmit={handleSubmit} className='space-y-6'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <Input
                type='text'
                label={t('buyDirect.form.firstName')}
                autoComplete='given-name'
                value={formData.first_name}
                onValueChange={(value) => handleChange(value, 'first_name')}
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
              <Input
                type='text'
                label={t('buyDirect.form.lastName')}
                autoComplete='family-name'
                value={formData.last_name}
                onValueChange={(value) => handleChange(value, 'last_name')}
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
            </div>

            <Input
              type='email'
              label={t('buyDirect.form.email')}
              autoComplete='email'
              value={formData.email}
              onValueChange={(value) => handleChange(value, 'email')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            <Input
              type='tel'
              label={t('buyDirect.form.phone')}
              autoComplete='tel'
              value={formData.phone}
              onValueChange={(value) => handleChange(value, 'phone')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            {/* Vehicle Info */}
            <Autocomplete
              label={t('buyDirect.form.brand')}
              placeholder={t('buyDirect.form.brandPlaceholder')}
              selectedKey={formData.brand_id}
              onSelectionChange={(key) =>
                handleChange(key as string, 'brand_id')
              }
              isRequired
              variant='bordered'
              inputProps={{ classNames: inputClassNames }}
            >
              {brands.map((brand) => (
                <AutocompleteItem key={brand.id} value={brand.id}>
                  {brand.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>

            <Autocomplete
              label={t('buyDirect.form.model')}
              placeholder={t('buyDirect.form.modelPlaceholder')}
              selectedKey={formData.model_id}
              onSelectionChange={(key) =>
                handleChange(key as string, 'model_id')
              }
              isRequired
              isDisabled={!selectedBrandId}
              variant='bordered'
              inputProps={{ classNames: inputClassNames }}
            >
              {models.map((model) => (
                <AutocompleteItem
                  key={model.id.toString()}
                  value={model.id.toString()}
                >
                  {model.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>

            {/* Patente y versión, sólo en modo tasador (ticket #90): sin ellas
                la tasación compara contra autos de otro año o de otra versión.
                Los otros modos no cambian. */}
            {tasaAutomatico && (
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <Input
                  type='text'
                  label={t('buyDirect.form.plate')}
                  placeholder={
                    esBrasil
                      ? t('formsUi.buyDirect.platePlaceholderBr')
                      : t('buyDirect.form.platePlaceholder')
                  }
                  autoCapitalize='characters'
                  maxLength={10}
                  value={formData.license_plate}
                  onValueChange={(value) => handleChange(value, 'license_plate')}
                  ref={patenteRef}
                  isRequired
                  // `undefined` y no `false`: con `false` HeroUI toma la validez
                  // como controlada y se come el aviso de campo obligatorio.
                  isInvalid={patenteInvalida || undefined}
                  errorMessage={
                    patenteInvalida
                      ? esBrasil
                        ? t('formsUi.buyDirect.plateInvalidBr')
                        : t('buyDirect.form.plateInvalid')
                      : undefined
                  }
                  variant='bordered'
                  classNames={{
                    ...inputClassNames,
                    input: [inputClassNames?.input, 'uppercase'].filter(Boolean).join(' '),
                  }}
                />
                <Input
                  type='text'
                  label={t('buyDirect.form.version')}
                  placeholder={t('buyDirect.form.versionPlaceholder')}
                  maxLength={60}
                  value={formData.version}
                  onValueChange={(value) => handleChange(value, 'version')}
                  variant='bordered'
                  classNames={inputClassNames}
                />
              </div>
            )}

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <Input
                type='text'
                label={t('buyDirect.form.year')}
                inputMode='numeric'
                value={formData.vehicle_year}
                onValueChange={(value) => handleChange(value, 'vehicle_year')}
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
              <Input
                type='text'
                label={t('buyDirect.form.mileage')}
                inputMode='numeric'
                value={formData.vehicle_mileage}
                onValueChange={(value) =>
                  handleChange(value, 'vehicle_mileage')
                }
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
            </div>

            <Select
              label={t('buyDirect.form.condition')}
              selectedKeys={
                formData.condition_id ? [formData.condition_id] : []
              }
              onChange={(e) => handleChange(e.target.value, 'condition_id')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            >
              {conditions.map((condition) => (
                <SelectItem
                  key={condition.id.toString()}
                  value={condition.id.toString()}
                >
                  {/* Solo la etiqueta: el value sigue siendo el id. En Chile, el nombre de siempre. */}
                  {etiquetaCatalogo('condition', condition.name, client?.country, condition.name)}
                </SelectItem>
              ))}
            </Select>

            {/* El precio se lo pedimos a la persona sólo en el modo 'visitante'.
                En 'tasador' no se pide a propósito: preguntarle cuánto quiere
                por su auto y después decirle cuánto vale es al revés, y el
                número que ella escribe condiciona lo que espera recibir. */}
            {pidePrecio && (
              <Input
                type='text'
                label={t('buyDirect.form.askingPrice')}
                inputMode='numeric'
                value={formData.asking_price}
                onValueChange={(value) => handleChange(value, 'asking_price')}
                startContent={symbol}
                variant='bordered'
                isRequired
                classNames={inputClassNames}
              />
            )}

            <Textarea
              label={t('buyDirect.form.message')}
              value={formData.message}
              onValueChange={(value) => handleChange(value, 'message')}
              minRows={4}
              variant='bordered'
              classNames={inputClassNames}
            />

            <Button
              type='submit'
              color='primary'
              fullWidth
              className={'h-12 sm:h-10 ' + (embedded ? 'font-semibold !text-white hover:opacity-90' : 'font-semibold bg-primary text-secondary hover:bg-primary/90 dark:bg-primary dark:text-secondary dark:hover:bg-primary/90')}
              style={buttonStyle}
              isLoading={loading}
            >
              {t('buyDirect.form.submit')}
            </Button>
          </form>
        </div>

        {/* Information Section */}
        {!embedded && !hasBuilderStyles && (
        <div className={infoCardClass} style={infoCardStyle}>
          <div className='space-y-8'>
            <h2 className={headingClass} style={headingStyle}>
              {t('buyDirect.info.advantagesTitle')}
            </h2>

            <div>
              <h3 className={subHeadingClass} style={headingStyle}>
                {t('buyDirect.info.benefitsTitle')}
              </h3>
              <ul className={`mt-2 space-y-2 list-disc pl-5 ${bodyClass}`} style={bodyStyle}>
                <li>{t('buyDirect.info.benefitsList.item1')}</li>
                <li>{t('buyDirect.info.benefitsList.item2')}</li>
                <li>{t('buyDirect.info.benefitsList.item3')}</li>
                <li>{t('buyDirect.info.benefitsList.item4')}</li>
                <li>{t('buyDirect.info.benefitsList.item5')}</li>
              </ul>
            </div>

            <div>
              <h3 className={subHeadingClass} style={headingStyle}>
                {t('buyDirect.info.processTitle')}
              </h3>
              <ol className={`mt-2 space-y-2 list-decimal pl-5 ${bodyClass}`} style={bodyStyle}>
                <li>{t('buyDirect.info.processList.step1')}</li>
                <li>{t('buyDirect.info.processList.step2')}</li>
                <li>{t('buyDirect.info.processList.step3')}</li>
                <li>{t('buyDirect.info.processList.step4')}</li>
                <li>{t('buyDirect.info.processList.step5')}</li>
              </ol>
            </div>

            <div>
              <h3 className={subHeadingClass} style={headingStyle}>
                {t('buyDirect.info.directContactTitle')}
              </h3>
              <p className={`mt-2 ${bodyClass}`} style={bodyStyle}>
                {t('buyDirect.info.emailLabel')}: {client?.contact?.email}
                <br />
                {t('buyDirect.info.phoneLabel')}: {client?.contact?.phone}
              </p>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Success Modal */}
      {/* En modo tasador se avisa que la estimación llega por correo. Sin este
          aviso la persona cierra la pestaña esperando ver un número en pantalla
          que nunca aparece, porque la tasación tarda alrededor de medio minuto
          y se manda por mail. */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        leadType={LeadTypes.BUY_DIRECT}
        customMessage={
          tasaAutomatico
            ? t('formsUi.buyDirect.successAppraisal')
            : undefined
        }
      />
    </div>
  );
};

export default BuyDirectForm;
